<template>
    <div>
        <div
            :id="`swiper-${sliderName}`"
            class="swiper relative overflow-hidden rounded-12">
            <div class="swiper-wrapper">
                <div
                    v-for="image in images"
                    :key="image.alt || 'portfolio image'"
                    class="swiper-slide relative !h-auto max-md:!w-[17.5rem]">
                    <picture>
                        <source
                            v-if="image.webp_url"
                            type="image/webp"
                            :srcset="
                                getMediaLink(image.webp_url || '') || ''
                            " />

                        <source
                            v-else-if="!image.webp_url && !image.url"
                            type="image/webp"
                            srcset="
                                /assets/images/ui/placeholder/webp/cromi.webp
                            " />

                        <img
                            :width="image.width || 416"
                            :height="image.height || 234"
                            :alt="image.alt || String(sliderName)"
                            :src="
                                getMediaLink(image.url || '') ||
                                '/assets/images/ui/placeholder/jpg-png/cromi.png'
                            "
                            class="aspect-video w-full object-cover max-lg:rounded-12" />
                    </picture>

                    <span
                        v-if="image.alt"
                        v-html="image.alt"
                        class="absolute bottom-0 left-0 rounded-bl-12 rounded-tr-12 bg-video-title p-3 text-12/140 font-medium text-ash-200 md:px-8 md:py-6 md:text-24/133 md:font-semibold" />
                </div>
            </div>
            <div class="absolute bottom-6 right-6 z-30 hidden gap-3 md:flex">
                <ArrowButton
                    :id="`prev-button-${sliderName}`"
                    class="rotate-180" />
                <ArrowButton :id="`next-button-${sliderName}`" />
            </div>
            <div
                class="absolute left-[calc(100%_-_15rem)] top-[calc(100%_-_10rem)] z-20 h-[20rem] w-[35rem] rounded-full bg-accent blur-[5rem] max-md:hidden" />
        </div>
        <div
            v-show="images.length > 1"
            :class="`swiper-${sliderName}-pagination`"
            class="mt-5 flex items-center justify-center" />
    </div>
</template>

<script setup lang="ts">
import { Swiper } from 'swiper'
import 'swiper/css'
import { EffectFade, Navigation, Pagination } from 'swiper/modules'
import type { Image } from '~/types/global.types'

import ArrowButton from '~/components/base/arrow-button/ArrowButton.vue'

const props = defineProps({
    images: {
        type: Array as PropType<Image[]>,
        required: true,
    },
    sliderName: {
        type: [String, Number] as PropType<string | number>,
        required: true,
    },
})

onMounted(() => {
    new Swiper(`#swiper-${props.sliderName}`, {
        modules: [Navigation, EffectFade, Pagination],
        navigation: {
            prevEl: `#prev-button-${props.sliderName}`,
            nextEl: `#next-button-${props.sliderName}`,
        },
        pagination: {
            el: `.swiper-${props.sliderName}-pagination`,
            clickable: true,
            bulletClass: 'bullet',
            bulletActiveClass: 'bullet--active',
            renderBullet: function (index: number, className: string) {
                return `<button class="${className}" aria-label="select ${
                    index + 1
                } slide"></button>`
            },
        },
        slidesPerView: 'auto',
        spaceBetween: 20,
        breakpoints: {
            744: {
                slidesPerView: 1,
                spaceBetween: 0,
            },
        },
    })
})
</script>
